import { useEffect } from "react";
import { z } from "zod";
import { ErrorButton } from "../../../../../../components/Button/ErrorButton.tsx";
import { SelectionDropdown } from "../../../../../../components/Dropdown/SelectionDropdown.tsx";
import { Form } from "../../../../../../components/Form/Form.tsx";
import { useForm } from "../../../../../../components/Form/FormContext.ts";
import { DEFAULT_GENERATE_GUIDANCE_SCALE } from "../../../../../boardState/stores/settings/generateSettingsStorage.ts";
import { useDebugMode } from "../../../../../components/DebugMode/hooks/useDebugMode.ts";
import { PanelLayout } from "../../../../components/Layout/PanelLayout.tsx";
import { PanelSection } from "../../../../components/Panel/PanelSection.tsx";
import { ActionsBar } from "../../../../components/Panel/Settings/ActionsBar.tsx";
import { ModelSelectionSection } from "../../../../components/Panel/Settings/ModelSelectionSection.tsx";
import { PromptSection } from "../../../../components/Panel/Settings/PromptSection.tsx";
import { SliderSubsection } from "../../../../components/Panel/SliderSubsection.tsx";
import { SwitchSubsection } from "../../../../components/Panel/SwitchSubsection.tsx";
import { WrappedPanelSection } from "../../../../components/Panel/WrappedPanelSection.tsx";
import { GENERATION_RATIOS } from "../../../../constants.ts";
import { GENERATION_RATIO_DISPLAY } from "../../../../display.ts";
import { useBoardUuid } from "../../../../hooks/useBoardUuid.ts";
import { getErrorsFiltered } from "../../../../utils/getErrorsFiltered.ts";
import { usePromptWarnings } from "../../../hooks/usePromptWarnings.ts";
import { scrollCreateWorkspaceGridToTop } from "../../utils/scrollCreateWorkspaceGridToTop.ts";
import { useGenerateImageFromPrompt } from "./hooks/useGenerateImageFromPrompt.ts";
import { useGenerateSettings } from "./hooks/useGenerateSettings.ts";

export const GenerateSettings = () => {
  const {
    mutation: generateImageFromPrompt,
    isLoading: isImageFromPromptGenerationLoading,
  } = useGenerateImageFromPrompt({
    onSuccess: scrollCreateWorkspaceGridToTop,
  });

  return (
    <Form
      schema={zGenerateSettings}
      initialValues={{
        prompt: "",
        areSelectedModelsValid: false,
      }}
      onSubmit={() => {
        // FIXME: make the mutation async
        generateImageFromPrompt();
        return Promise.resolve(true);
      }}
    >
      <GenerateSettingsFormContent
        isGenerationLoading={isImageFromPromptGenerationLoading}
      />
    </Form>
  );
};

const zGenerateSettings = z.object({
  prompt: z.string().min(1, { message: "Please enter a prompt" }),
  areSelectedModelsValid: z.boolean().refine((val) => val, {
    message:
      "The selected models are not valid, please try again by reseting the selection.",
  }),
});
type GenerateValues = z.input<typeof zGenerateSettings>;

export const GenerateSettingsFormContent = ({
  isGenerationLoading,
}: {
  isGenerationLoading: boolean;
}) => {
  const boardUuid = useBoardUuid();

  const { tool, settings, setSettings, areSelectedModelsValid, modelActions } =
    useGenerateSettings(boardUuid);

  const { setValues, submit, useError } = useForm<GenerateValues>();

  const selectedModelsValidError = useError((v) => v.areSelectedModelsValid);

  const promptEmptyError = useError((v) => v.prompt);

  useEffect(() => {
    setValues({
      areSelectedModelsValid,
    });
  }, [setValues, areSelectedModelsValid]);

  useEffect(() => {
    setValues({ prompt: settings.prompt });
  }, [setValues, settings.prompt]);

  const warnings = usePromptWarnings({
    prompt: settings.prompt,
    selectedModels: settings.selectedModels.models,
  });

  const isDebugModeOn = useDebugMode();

  return (
    <PanelLayout
      bottomChildren={
        <ActionsBar
          isLoading={isGenerationLoading}
          onPrimaryActionClick={submit}
          primaryActionButtonContent="Generate"
          intercomArticle="improve_the_quality_of_generated_images"
          errors={getErrorsFiltered([selectedModelsValidError])}
          errorsChildren={
            selectedModelsValidError && (
              <ErrorButton
                onClick={() => {
                  modelActions.reset();
                }}
              >
                Reset model selection
              </ErrorButton>
            )
          }
        />
      }
    >
      <PromptSection
        sectionTitle="Prompt"
        prompt={settings.prompt}
        selectedModels={settings.selectedModels.models}
        warnings={warnings}
        setPrompt={(newPrompt) =>
          setSettings({
            prompt: newPrompt,
          })
        }
        placeholder="Describe what you want to see in your images"
        autoFocus
        onEnterKeyDown={submit}
        promptEnhancerProps={{
          displayed: true,
          disabled: false,
        }}
        promptEditorProps={{
          displayed: true,
          disabled: false,
        }}
        promptFromImageGeneratorProps={{
          displayed: true,
          disabled: false,
        }}
        errors={getErrorsFiltered([promptEmptyError])}
        className="min-h-[140px]"
      />
      <ModelSelectionSection
        tool={tool}
        doesObjectModelSupportUpload
        doesCharacterModelSupportUpload
        doesStyleModelSupportUpload
      />
      <PanelSection
        title="Ratio"
        rightItem={
          <SelectionDropdown
            value={settings.generation_ratio}
            onChange={(generationRatio) =>
              setSettings({
                generation_ratio: generationRatio,
              })
            }
            options={GENERATION_RATIOS.map((generationRatio) => ({
              value: generationRatio,
              ...GENERATION_RATIO_DISPLAY[generationRatio],
            }))}
            size="sm"
            align="end"
            buttonClassName="w-[94px]"
          />
        }
      />
      {isDebugModeOn && (
        <WrappedPanelSection title="Debug parameters">
          <div className="flex-col gap-150">
            <SwitchSubsection
              switchName="Enforce photorealistic effects"
              value={settings.enforce_photo}
              onChange={(enforcePhoto) =>
                setSettings({
                  enforce_photo: enforcePhoto,
                })
              }
            />
            {settings.enforce_photo && (
              <SwitchSubsection
                switchName="Add photorealistic effects"
                value={settings.photo_enabled}
                onChange={(photoEnabled) =>
                  setSettings({
                    photo_enabled: photoEnabled,
                  })
                }
              />
            )}
            <SliderSubsection
              value={Math.round(settings.guidance_scale * 100)}
              min={0}
              max={100}
              onChange={(guidanceScalePercentage) =>
                setSettings({
                  guidance_scale: guidanceScalePercentage / 100,
                })
              }
              sliderName="Guidance scale"
              defaultValue={DEFAULT_GENERATE_GUIDANCE_SCALE * 100}
              sliderInformationSection={
                <div className="flex-col gap-200 body-md-default text-primary">
                  <div>
                    Following range will be applied to the guidance scale:
                  </div>
                  <div>
                    - <span className="body-md-semibold">Flux (Photo)</span>: 0%
                    : 2.5, 100% : 4.5
                  </div>
                  <div>
                    -{" "}
                    <span className="body-md-semibold">
                      Flux (Illustration)
                    </span>
                    : 0% : 4, 100% : 6
                  </div>
                </div>
              }
            />
          </div>
        </WrappedPanelSection>
      )}
    </PanelLayout>
  );
};
